import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Swords, 
  Users, 
  Copy, 
  Clock, 
  Hash, 
  Medal, 
  Crown,
  CheckCircle,
  XCircle,
  User,
  BarChart2,
  Loader2,
  PlayCircle,
  RefreshCw,
  Globe,
  Lock
} from 'lucide-react';
import { useBattle } from '../context/BattleContext';
import { useUser } from '../context/UserContext';
import { BattleParticipant } from '../types/battle';
import toast from 'react-hot-toast';

// Module-level helpers to avoid nested function definitions
const getParticipantStatus = (participant: BattleParticipant): string => {
  if (participant.isHost) return 'Host';
  if (participant.isReady) return 'Ready';
  return 'Not Ready';
};

const getRankColor = (index: number): string => {
  if (index === 0) return 'text-yellow-500';
  if (index === 1) return 'text-slate-400';
  if (index === 2) return 'text-amber-700';
  return 'text-textMuted';
};

const getAccuracyColor = (accuracy: number): string => {
  if (accuracy > 95) return 'text-green-500';
  if (accuracy > 90) return 'text-yellow-500';
  return 'text-red-500';
};

const BattleRoom: React.FC = () => { // NOSONAR - cognitive complexity inherent to real-time battle UI
  const navigate = useNavigate();
  const { state: battleState, leaveRoom, startBattle, updateParticipant, toggleReady } = useBattle();
  const { state: userState } = useUser();
  const { activeRoom } = battleState;
  const currentUser = userState.user;
  
  // Refs
  const typingInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Typing state
  const [currentInput, setCurrentInput] = useState('');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [completedWords, setCompletedWords] = useState<string[]>([]);
  const [errors, setErrors] = useState<Set<number>>(new Set());
  
  // Stats state
  const [startTime, setStartTime] = useState<number | null>(null);
  const [wordsTyped, setWordsTyped] = useState(0);
  const [charsTyped, setCharsTyped] = useState(0);
  const [correctChars, setCorrectChars] = useState(0);
  const [wpm, setWpm] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  
  // UI state
  const [copied, setCopied] = useState(false);
  const [countdownValue, setCountdownValue] = useState(3);
  
  // Check if user is in a room
  useEffect(() => {
    if (!activeRoom) {
      navigate('/battle');
    }
  }, [activeRoom, navigate]);
  
  // Find current participant (user)
  const currentParticipant = activeRoom?.participants.find(
    p => p.userId === currentUser?.id
  );
  
  // Check if user is host
  const isHost = currentUser != null && activeRoom?.hostId === currentUser.id;
  
  // Get text from room if available
  const battleText = activeRoom?.text || '';
  const battleWords = battleText.split(' ');
  
  // Time left calculation for time mode
  const [timeLeft, setTimeLeft] = useState(activeRoom?.settings.duration || 60);
  
  // Handle timer countdown
  useEffect(() => {
    if (!activeRoom) return;
    
    // Handle countdown state
    if (activeRoom.status === 'countdown') {
      const countdownInterval = setInterval(() => {
        setCountdownValue(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(countdownInterval);
    }
    
    // Reset typing state when battle starts
    if (activeRoom.status === 'active' && !startTime) {
      setStartTime(Date.now());
      if (typingInputRef.current) {
        typingInputRef.current.focus();
      }
    }
    
    // Timer for time mode
    if (activeRoom.status === 'active' && activeRoom.settings.mode === 'time') {
      const timerInterval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Time's up
            const calculatedProgress = 100;
            updateParticipant({
              userId: currentUser?.id as string,
              progress: calculatedProgress,
              stats: {
                wpm,
                accuracy,
                errors: errors.size,
                charsTyped,
                wordsTyped,
                correctChars,
                totalChars: charsTyped
              }
            });
            clearInterval(timerInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timerInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoom?.status, startTime, activeRoom?.settings.mode]);
  
  // Calculate and update stats
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!startTime || activeRoom?.status !== 'active') return;
    
    const updateStats = () => {
      const elapsedTime = (Date.now() - startTime) / 1000 / 60; // in minutes
      const currentWpm = Math.round(correctChars / 5 / elapsedTime || 0);
      const currentAccuracy = charsTyped > 0 ? (correctChars / charsTyped) * 100 : 100;
      
      setWpm(currentWpm);
      setAccuracy(currentAccuracy);
      
      // Calculate progress based on mode
      let calculatedProgress = 0;
      
      if (activeRoom?.settings.mode === 'time') {
        const totalTime = activeRoom.settings.duration || 60;
        const elapsedSeconds = (Date.now() - startTime) / 1000;
        calculatedProgress = Math.min((elapsedSeconds / totalTime) * 100, 100);
      } else if (activeRoom?.settings.mode === 'words') {
        const totalWords = activeRoom.settings.wordCount || 50;
        calculatedProgress = Math.min((wordsTyped / totalWords) * 100, 100);
      }
      
      // Update participant stats
      updateParticipant({
        userId: currentUser?.id as string,
        progress: calculatedProgress,
        stats: {
          wpm: currentWpm,
          accuracy: currentAccuracy,
          errors: errors.size,
          charsTyped,
          wordsTyped,
          correctChars,
          totalChars: charsTyped
        }
      });
    };
    
    // Update stats every second
    const statsInterval = setInterval(updateStats, 1000);
    return () => clearInterval(statsInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startTime, 
    correctChars, 
    charsTyped, 
    wordsTyped, 
    errors.size, 
    activeRoom?.settings.mode, 
    activeRoom?.settings.wordCount,
    activeRoom?.settings.duration,
    activeRoom?.status
  ]);
  
  // Handle keyboard input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => { // NOSONAR
    if (activeRoom?.status !== 'active') return;
    
    // Space key handling for word completion
    if (e.key === ' ') {
      e.preventDefault();
      
      // Skip if the input is empty
      if (currentInput.trim() === '') return;
      
      // Check if the word is correct
      const currentWord = battleWords[currentWordIndex];
      const isCorrect = currentInput.trim() === currentWord;
      
      // Update stats
      setCharsTyped(prev => prev + currentInput.length + 1); // +1 for space
      if (isCorrect) {
        setCorrectChars(prev => prev + currentWord.length + 1); // +1 for space
      }
      
      // Update completed words
      setCompletedWords([...completedWords, currentInput]);
      
      // Move to next word
      setCurrentWordIndex(currentWordIndex + 1);
      setCurrentCharIndex(0);
      setCurrentInput('');
      setWordsTyped(prev => prev + 1);
      
      // Check if test is complete for word mode
      if (activeRoom.settings.mode === 'words' && 
          currentWordIndex + 1 >= (activeRoom.settings.wordCount || 50)) {
        // Complete the test
        const calculatedProgress = 100;
        updateParticipant({
          userId: currentUser?.id as string,
          progress: calculatedProgress,
          stats: {
            wpm,
            accuracy,
            errors: errors.size,
            charsTyped: charsTyped + currentInput.length + 1,
            wordsTyped: wordsTyped + 1,
            correctChars: correctChars + (isCorrect ? currentWord.length + 1 : 0),
            totalChars: charsTyped + currentInput.length + 1
          }
        });
      }
      
      return;
    }
    
    // Backspace handling
    if (e.key === 'Backspace') {
      if (currentInput.length > 0) {
        setCurrentInput(currentInput.slice(0, -1));
        setCurrentCharIndex(Math.max(0, currentCharIndex - 1));
      }
      return;
    }
    
    // Skip control and non-printable keys
    if (e.ctrlKey || e.altKey || e.metaKey || e.key.length !== 1) return;
    
    // Add the typed character
    const typedChar = e.key;
    const expectedChar = battleWords[currentWordIndex]?.[currentCharIndex] || '';
    
    // Update character stats
    setCurrentInput(currentInput + typedChar);
    setCurrentCharIndex(currentCharIndex + 1);
    
    // Check if character is correct
    if (typedChar === expectedChar) {
      // Correct character
      setCorrectChars(prev => prev + 1);
    } else {
      // Incorrect character - update errors
      const newErrors = new Set(errors);
      newErrors.add(completedWords.join(' ').length + currentInput.length);
      setErrors(newErrors);
    }
  };
  
  // Handle copy invite code
  const handleCopyInviteCode = () => {
    if (!activeRoom) return;
    
    navigator.clipboard.writeText(activeRoom.inviteCode)
      .then(() => {
        setCopied(true);
        toast.success('Invite code copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        toast.error('Failed to copy invite code');
      });
  };
  
  // Handle leave room
  const handleLeaveRoom = () => {
    leaveRoom();
    navigate('/battle');
  };
  
  // Handle start battle (host only)
  const handleStartBattle = () => {
    if (!isHost) return;
    
    startBattle()
      .then(() => {
        setCountdownValue(3);
      })
      .catch(error => {
        console.error('Failed to start battle:', error);
      });
  };
  
  // Handle ready status toggle
  const handleToggleReady = () => {
    toggleReady().catch(error => {
      console.error('Failed to toggle ready status:', error);
    });
  };
  
  // Check if all participants are ready
  const allParticipantsReady = activeRoom?.participants.every(p => 
    p.isHost || p.isReady
  );
  
  // Render the battle text display for typing
  const renderTextDisplay = () => {
    if (!battleText) return null;
    
    const completedText = completedWords.join(' ');
    const currentWord = battleWords[currentWordIndex] || '';
    const upcomingWords = battleWords.slice(currentWordIndex + 1).join(' ');
    
    return (
      <div className="font-mono text-lg leading-relaxed relative">
        {/* Completed words */}
        <span className="text-textMuted/80">{completedText}{completedText && ' '}</span>
        
        {/* Current word */}
        <span className="relative">
          {/* Correctly typed part */}
          <span className="text-accent">
            {currentInput.split('').map((char, i) => {
              const isCorrect = char === currentWord[i];
              return (
                <span key={`char-${currentWordIndex}-${i}-${char}`} className={isCorrect ? 'text-accent' : 'text-red-500 bg-red-500/20'}>
                  {char}
                </span>
              );
            })}
          </span>
          
          {/* Current character cursor */}
          <span className="bg-accent/50 text-primary absolute animate-blink">
            {currentWord[currentInput.length] || ' '}
          </span>
          
          {/* Remaining characters in word */}
          <span className="text-text/50">
            {currentWord.substring(currentInput.length + 1)}
          </span>
        </span>
        
        {/* Upcoming words */}
        <span className="text-text/30"> {upcomingWords}</span>
      </div>
    );
  };
  
  // Render participants list
  const renderParticipantsList = () => {
    if (!activeRoom) return null;
    
    // Sort participants by position (for active battles) or by host status and ready status
    const sortedParticipants = [...activeRoom.participants].sort((a, b) => {
      if (activeRoom.status === 'active' || activeRoom.status === 'completed') {
        // Sort by progress/position
        return b.progress - a.progress;
      }
      
      // Sort by host first, then ready status
      if (a.isHost && !b.isHost) return -1;
      if (!a.isHost && b.isHost) return 1;
      if (a.isReady && !b.isReady) return -1;
      if (!a.isReady && b.isReady) return 1;
      return 0;
    });
    
    return (
      <div className="space-y-2">
        {sortedParticipants.map((participant, index) => (
          <div 
            key={participant.userId} 
            className={`bg-secondary/30 rounded-lg p-3 border ${
              participant.userId === currentUser?.id
                ? 'border-accent/30'
                : 'border-transparent'
            }`}
          >
            <div className="flex justify-between items-center">
              {/* User info */}
              <div className="flex items-center space-x-2">
                {activeRoom.status === 'active' && (
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${(() => {
                    if (index === 0) return 'bg-yellow-500 text-primary';
                    if (index === 1) return 'bg-slate-400 text-primary';
                    if (index === 2) return 'bg-amber-700 text-primary';
                    return 'bg-secondary text-textMuted';
                  })()}`}>
                    {index + 1}
                  </div>
                )}
                <span className="font-medium flex items-center">
                  {participant.isHost && (
                    <Crown className="w-4 h-4 text-accent mr-1" />
                  )}
                  {participant.username}
                  {participant.userId === currentUser?.id && (
                    <span className="text-xs text-textMuted ml-2">(You)</span>
                  )}
                </span>
                {activeRoom.status === 'waiting' && (
                  <span className={`w-2 h-2 rounded-full ${
                    participant.isReady ? 'bg-green-500' : 'bg-yellow-500'
                  }`}></span>
                )}
              </div>
              
              {/* Status/Stats */}
              <div className="flex items-center">
                {(() => {
                  if (activeRoom.status === 'waiting') {
                    return (
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary/50 text-textMuted">
                        {getParticipantStatus(participant)}
                      </span>
                    );
                  }
                  
                  if (activeRoom.status === 'active') {
                    return (
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-1">
                          <BarChart2 className="w-3 h-3 text-accent" />
                          <span className="text-accent font-medium">{participant.stats.wpm}</span>
                        </div>
                        <div className="w-20 h-2 bg-secondary/50 rounded-full">
                          <div 
                            className="h-2 bg-accent rounded-full"
                            style={{ width: `${participant.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  }
                  
                  if (activeRoom.status === 'completed') {
                    return (
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm ${getRankColor(index)}`}>
                          {participant.stats.wpm} WPM
                        </span>
                        <span className={`text-xs ${getAccuracyColor(participant.stats.accuracy)}`}>
                          {participant.stats.accuracy.toFixed(1)}%
                        </span>
                      </div>
                    );
                  }
                  
                  return null;
                })()}
              </div>
            </div>
            
            {/* Progress bar */}
            {(activeRoom.status === 'active' || activeRoom.status === 'completed') && (
              <div className="w-full h-1 bg-secondary/30 mt-2 rounded-full overflow-hidden">
                <div 
                  className={`h-1 ${(() => {
                    if (index === 0) return 'bg-yellow-500';
                    if (index === 1) return 'bg-slate-400';
                    if (index === 2) return 'bg-amber-700';
                    return 'bg-accent/50';
                  })()}`}
                  style={{ width: `${participant.progress}%` }}
                ></div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };
  
  // Render countdown overlay
  const renderCountdown = () => {
    if (activeRoom?.status !== 'countdown') return null;
    
    return (
      <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-10 rounded-xl">
        <div className="text-center">
          <div className="text-6xl font-bold text-accent mb-4 animate-pulse">
            {countdownValue}
          </div>
          <p className="text-textMuted">Get ready to type!</p>
        </div>
      </div>
    );
  };
  
  // Render results (when completed)
  const renderResults = () => {
    if (activeRoom?.status !== 'completed') return null;
    
    // Sort participants by WPM
    const rankedParticipants = [...activeRoom.participants].sort((a, b) => 
      b.stats.wpm - a.stats.wpm
    );
    
    // Find current user's position
    const currentUserRank = rankedParticipants.findIndex(
      p => p.userId === currentUser?.id
    ) + 1;
    
    return (
      <div className="bg-cardBg rounded-xl shadow-lg border border-secondary overflow-hidden">
        <div className="p-6 border-b border-secondary">
          <h3 className="text-2xl font-bold flex items-center">
            <Medal className="w-6 h-6 mr-2 text-accent" />
            Battle Results
          </h3>
        </div>
        
        {/* Winner podium */}
        <div className="px-6 py-8">
          <div className="flex items-end justify-center space-x-4 mb-8">
            {/* Second place */}
            {rankedParticipants.length > 1 && (
              <div className="text-center pb-4">
                <div className="bg-slate-400 w-20 h-24 flex items-center justify-center rounded-t-lg">
                  <div className="bg-primary rounded-full w-12 h-12 flex items-center justify-center">
                    <User className="w-6 h-6 text-slate-400" />
                  </div>
                </div>
                <div className="bg-slate-400/20 p-3 rounded-b-lg">
                  <div className="font-bold text-sm text-slate-400">
                    {rankedParticipants[1]?.username}
                  </div>
                  <div className="text-slate-400 text-xs">
                    {rankedParticipants[1]?.stats.wpm} WPM
                  </div>
                </div>
                <div className="mt-2 text-2xl">🥈</div>
              </div>
            )}
            
            {/* First place */}
            {rankedParticipants.length > 0 && (
              <div className="text-center">
                <div className="bg-yellow-500 w-24 h-32 flex items-center justify-center rounded-t-lg">
                  <div className="bg-primary rounded-full w-14 h-14 flex items-center justify-center">
                    <Crown className="w-8 h-8 text-yellow-500" />
                  </div>
                </div>
                <div className="bg-yellow-500/20 p-3 rounded-b-lg">
                  <div className="font-bold text-yellow-500">
                    {rankedParticipants[0]?.username}
                  </div>
                  <div className="text-yellow-500 text-sm">
                    {rankedParticipants[0]?.stats.wpm} WPM
                  </div>
                </div>
                <div className="mt-2 text-3xl">🏆</div>
              </div>
            )}
            
            {/* Third place */}
            {rankedParticipants.length > 2 && (
              <div className="text-center pb-8">
                <div className="bg-amber-700 w-20 h-20 flex items-center justify-center rounded-t-lg">
                  <div className="bg-primary rounded-full w-12 h-12 flex items-center justify-center">
                    <User className="w-6 h-6 text-amber-700" />
                  </div>
                </div>
                <div className="bg-amber-700/20 p-3 rounded-b-lg">
                  <div className="font-bold text-sm text-amber-700">
                    {rankedParticipants[2]?.username}
                  </div>
                  <div className="text-amber-700 text-xs">
                    {rankedParticipants[2]?.stats.wpm} WPM
                  </div>
                </div>
                <div className="mt-2 text-2xl">🥉</div>
              </div>
            )}
          </div>
          
          {/* User's result */}
          <div className="bg-secondary/30 p-4 rounded-lg border border-accent/20 mb-6">
            <div className="text-sm text-textMuted mb-1">Your Performance</div>
            <div className="flex justify-between items-center">
              <div className="font-bold text-lg flex items-center">
                <span className="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center mr-2 text-sm">
                  {currentUserRank}
                </span>
                {currentUser?.username || 'You'}
              </div>
              <div className="text-right">
                <div className="text-accent font-bold">{currentParticipant?.stats.wpm || 0} WPM</div>
                <div className="text-sm text-textMuted">{currentParticipant?.stats.accuracy.toFixed(1) || 100}% accuracy</div>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-4">
            <button
              onClick={handleLeaveRoom}
              className="flex-1 py-3 bg-secondary hover:bg-secondary/70 text-text rounded-lg transition-colors flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Leave Room
            </button>
            <button
              onClick={() => navigate('/battle')}
              className="flex-1 py-3 bg-accent hover:bg-yellow-500 text-primary font-semibold rounded-lg transition-colors flex items-center justify-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              New Battle
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  if (!activeRoom) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 text-accent animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto pb-20" ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={handleLeaveRoom}
            className="mr-4 p-2 hover:bg-secondary/50 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-textMuted" />
          </button>
          <h1 className="text-3xl font-bold flex items-center">
            <Swords className="w-8 h-8 mr-3 text-accent" />
            {activeRoom.name}
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-textMuted" />
            <span className="text-text">{activeRoom.participants.length}/{activeRoom.maxParticipants}</span>
          </div>
          {!activeRoom.isPublic && (
            <div className="relative">
              <button
                onClick={handleCopyInviteCode}
                className="flex items-center space-x-1 py-1 px-3 bg-secondary/30 hover:bg-secondary/50 rounded-lg text-sm transition-colors"
              >
                <span className="font-mono">{activeRoom.inviteCode}</span>
                <Copy className="w-4 h-4 text-textMuted" />
              </button>
              {copied && (
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-accent text-primary text-xs py-1 px-2 rounded">
                  Copied!
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Participants */}
        <div>
          <div className="bg-cardBg rounded-xl shadow-lg border border-secondary p-6 mb-6">
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Participants ({activeRoom.participants.length})
            </h3>
            {renderParticipantsList()}
          </div>
          
          {/* Room Info */}
          <div className="bg-cardBg rounded-xl shadow-lg border border-secondary p-6">
            <h3 className="text-lg font-bold mb-4">Battle Info</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-secondary/20 p-3 rounded-lg">
                  <div className="text-xs text-textMuted">Mode</div>
                  <div className="font-medium flex items-center">
                    {activeRoom.settings.mode === 'time' ? (
                      <>
                        <Clock className="w-4 h-4 mr-1 text-accent" />
                        {activeRoom.settings.duration}s
                      </>
                    ) : (
                      <>
                        <Hash className="w-4 h-4 mr-1 text-accent" />
                        {activeRoom.settings.wordCount} words
                      </>
                    )}
                  </div>
                </div>
                <div className="bg-secondary/20 p-3 rounded-lg">
                  <div className="text-xs text-textMuted">Room Type</div>
                  <div className="font-medium flex items-center">{activeRoom.isPublic ? 
                      <Globe className="w-4 h-4 mr-1 text-green-500" /> : 
                      <Lock className="w-4 h-4 mr-1 text-yellow-500" />
                    }
                    {activeRoom.isPublic ? 'Public' : 'Private'}
                  </div>
                </div>
                <div className="bg-secondary/20 p-3 rounded-lg">
                  <div className="text-xs text-textMuted">Options</div>
                  <div className="text-sm text-textMuted flex flex-wrap gap-1">
                    {activeRoom.settings.includePunctuation && (
                      <span className="px-1 bg-secondary/30 rounded text-xs">Punctuation</span>
                    )}
                    {activeRoom.settings.includeNumbers && (
                      <span className="px-1 bg-secondary/30 rounded text-xs">Numbers</span>
                    )}
                    {!activeRoom.settings.includePunctuation && !activeRoom.settings.includeNumbers && (
                      <span>Standard text</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Status info */}
              <div className="text-center text-textMuted text-sm">
                {(() => {
                  if (activeRoom.status === 'waiting') {
                    return (
                      <div className="flex items-center justify-center">
                        <Clock className="w-4 h-4 mr-1 text-yellow-500" />
                        <span>Waiting for players</span>
                      </div>
                    );
                  }
                  
                  if (activeRoom.status === 'countdown') {
                    return (
                      <div className="flex items-center justify-center">
                        <Loader2 className="w-4 h-4 mr-1 text-yellow-500 animate-spin" />
                        <span>Starting battle...</span>
                      </div>
                    );
                  }
                  
                  if (activeRoom.status === 'active') {
                    return (
                      <div className="flex items-center justify-center">
                        <Swords className="w-4 h-4 mr-1 text-accent animate-pulse" />
                        <span>Battle in progress</span>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="flex items-center justify-center">
                      <Medal className="w-4 h-4 mr-1 text-yellow-500" />
                      <span>Battle completed</span>
                    </div>
                  );
                })()}
              </div>
              
              {/* Ready/Start/Leave buttons */}
              {activeRoom.status === 'waiting' && (
                <div className="flex space-x-3">
                  {isHost ? (
                    <button
                      onClick={handleStartBattle}
                      disabled={!allParticipantsReady || activeRoom.participants.length < 2}
                      className={`w-full py-3 rounded-lg transition-colors flex items-center justify-center ${
                        allParticipantsReady && activeRoom.participants.length >= 2
                          ? 'bg-accent hover:bg-yellow-500 text-primary font-semibold'
                          : 'bg-secondary/50 text-textMuted cursor-not-allowed'
                      }`}
                    >
                      <PlayCircle className="w-5 h-5 mr-2" />
                      Start Battle
                    </button>
                  ) : (
                    <button
                      onClick={handleToggleReady}
                      className={`w-full py-3 rounded-lg transition-colors flex items-center justify-center ${
                        currentParticipant?.isReady
                          ? 'bg-green-500 hover:bg-green-600 text-white font-semibold'
                          : 'bg-yellow-500 hover:bg-yellow-600 text-primary font-semibold'
                      }`}
                    >
                      {currentParticipant?.isReady ? (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Ready
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 mr-2" />
                          Not Ready
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Right column: Battle area */}
        <div className="lg:col-span-2">
          {activeRoom.status === 'completed' ? (
            renderResults()
          ) : (
            <div className="bg-cardBg rounded-xl shadow-lg border border-secondary p-6 mb-6 relative">
              {/* Battle header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold flex items-center">
                  <Swords className="w-5 h-5 mr-2" />
                  {(() => {
                    if (activeRoom.status === 'waiting') return 'Battle Starting Soon';
                    if (activeRoom.status === 'countdown') return 'Get Ready...';
                    return 'Battle In Progress';
                  })()}
                </h2>
                
                {activeRoom.status === 'active' && (
                  <div className="flex items-center space-x-4">
                    {/* Time left / Words completed */}
                    {activeRoom.settings.mode === 'time' ? (
                      <div className="bg-secondary/30 px-3 py-1 rounded-lg flex items-center">
                        <Clock className="w-4 h-4 mr-1 text-accent" />
                        <span className="font-mono font-medium">{timeLeft}s</span>
                      </div>
                    ) : (
                      <div className="bg-secondary/30 px-3 py-1 rounded-lg flex items-center">
                        <Hash className="w-4 h-4 mr-1 text-accent" />
                        <span className="font-mono font-medium">
                          {wordsTyped}/{activeRoom.settings.wordCount}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Render countdown overlay */}
              {renderCountdown()}
              
              {/* Text display area */}
              <div className={`bg-secondary/20 p-6 rounded-lg mb-6 ${
                activeRoom.status === 'waiting'
                  ? 'opacity-50'
                  : ''
              }`}>
                {activeRoom.status === 'waiting' ? (
                  <div className="text-center text-textMuted italic">
                    Text will appear here when the battle starts...
                  </div>
                ) : (
                  renderTextDisplay()
                )}
              </div>
              
              {/* Input field */}
              <div className="relative">
                <input
                  ref={typingInputRef}
                  type="text"
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={activeRoom.status !== 'active'}
                  className="w-full bg-secondary/30 border border-secondary rounded-lg p-3 focus:outline-none focus:border-accent font-mono"
                  placeholder={(() => {
                    if (activeRoom.status === 'waiting') return 'Waiting for host to start the battle...';
                    if (activeRoom.status === 'countdown') return 'Get ready to type...';
                    return 'Type here...';
                  })()}
                />
                
                {/* Stats display */}
                {activeRoom.status === 'active' && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-3">
                    <div className="text-accent font-medium text-sm">
                      {wpm} WPM
                    </div>
                    <div className={`text-sm ${(() => {
                      if (accuracy > 95) return 'text-green-500';
                      if (accuracy > 90) return 'text-yellow-500';
                      return 'text-red-500';
                    })()}`}>
                      {accuracy.toFixed(1)}%
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Leaderboard (during active battle) */}
          {activeRoom.status === 'active' && (
            <div className="bg-cardBg rounded-xl shadow-lg border border-secondary p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center">
                <BarChart2 className="w-5 h-5 mr-2" />
                Live Leaderboard
              </h3>
              
              <div className="space-y-3">
                {[...activeRoom.participants]
                  .sort((a, b) => b.stats.wpm - a.stats.wpm)
                  .slice(0, 5) // Show top 5 if there are many participants
                  .map((participant, index) => (
                    <div 
                      key={participant.userId}
                      className={`flex items-center bg-secondary/20 rounded-lg p-3 ${
                        participant.userId === currentUser?.id
                          ? 'border border-accent/30'
                          : ''
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs mr-3 ${(() => {
                        if (index === 0) return 'bg-yellow-500/20 text-yellow-500';
                        if (index === 1) return 'bg-slate-400/20 text-slate-400';
                        if (index === 2) return 'bg-amber-700/20 text-amber-700';
                        return 'bg-secondary/50 text-textMuted';
                      })()}`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <div className="font-medium">
                            {participant.username}
                            {participant.userId === currentUser?.id && (
                              <span className="text-xs text-textMuted ml-1">(You)</span>
                            )}
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-accent font-medium">
                              {participant.stats.wpm} WPM
                            </div>
                            <div className={`text-xs ${(() => {
                              if (participant.stats.accuracy > 95) return 'text-green-500';
                              if (participant.stats.accuracy > 90) return 'text-yellow-500';
                              return 'text-red-500';
                            })()}`}>
                              {participant.stats.accuracy.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                        <div className="w-full h-1 bg-secondary/30 mt-2 rounded-full overflow-hidden">
                          <div 
                            className={`h-1 ${(() => {
                              if (index === 0) return 'bg-yellow-500';
                              if (index === 1) return 'bg-slate-400';
                              if (index === 2) return 'bg-amber-700';
                              return 'bg-accent/50';
                            })()}`}
                            style={{ width: `${participant.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BattleRoom;
